import { useLayoutEffect, useRef, useState } from 'react';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Tooltip, Typography } from '@mui/material';
import { CustomUser } from 'api/models/CustomUser';
import { modelsStatusOptions } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { useUserGroups } from 'dashboard409a/hooks/useUserGroups';
import { ModelView, ViewUserId } from './ModelViewsSelector.props';
import { StyledViewButton } from './ModelViewsSelector.styles';
import { UserGroup } from '../UserGroups/UserGroups.types';

const TOTAL_STATUS_COUNT = modelsStatusOptions.length;

const CRITERIA_LABELS = {
  PERIOD: 'Period',
  PERIOD_ALL: 'All',
  PERIOD_CURRENT: 'Current',
  PERIOD_PAST: 'Past',
  PERIOD_UNASSIGNED: 'Unassigned',
  STATUS: 'Status',
  PREPARERS: 'Preparers',
  REVIEWERS: 'Reviewers',
  GROUPS: 'Groups',
  FROM: 'From',
  TO: 'To',
  DATE_PLACEHOLDER: '—',
  NO_FILTERS: 'No filters applied',
} as const;

interface ViewButtonWithTooltipProps {
  savedView: ModelView;
  currentViewId?: string | null;
  onClick: (event: React.MouseEvent<HTMLButtonElement>, savedView: ModelView) => void;
  staffUsers?: CustomUser[];
}

const toUserIds = (ids: (number | ViewUserId)[]): number[] =>
  ids.map(id => (typeof id === 'number' ? id : id.userId));

const formatUserList = (ids: (number | ViewUserId)[], users: CustomUser[]): string => {
  const userIds = toUserIds(ids);
  const [firstId, ...rest] = userIds;
  const first = users.find(u => u.id === firstId);
  const name = first
    ? [first.first_name, first.last_name].filter(Boolean).join(' ')
    : String(firstId);
  return rest.length > 0 ? `${name} +${rest.length}` : name;
};

const getGroupNames = (ids: ViewUserId[], groups: UserGroup[]): string[] => {
  const groupIds = new Set(ids.map(id => id.groupId).filter((g): g is string => g !== null));
  return groups.filter(g => groupIds.has(g.id)).map(g => g.name);
};

const buildCriteriaLines = (view: ModelView, staffUsers: CustomUser[], groups: UserGroup[]): string[] => {
  const lines: string[] = [];

  const sel = view.filterTypeSelection;
  if (sel) {
    if (sel.all) {
      lines.push(`${CRITERIA_LABELS.PERIOD}: ${CRITERIA_LABELS.PERIOD_ALL}`);
    } else {
      const periods: string[] = [];
      if (sel.current) {
        periods.push(CRITERIA_LABELS.PERIOD_CURRENT);
      }
      if (sel.past) {
        periods.push(CRITERIA_LABELS.PERIOD_PAST);
      }
      if (sel.unassigned) {
        periods.push(CRITERIA_LABELS.PERIOD_UNASSIGNED);
      }
      if (periods.length > 0) {
        lines.push(`${CRITERIA_LABELS.PERIOD}: ${periods.join(', ')}`);
      }
    }
  } else if (view.filterType) {
    lines.push(`${CRITERIA_LABELS.PERIOD}: ${view.filterType}`);
  }

  let statuses: string[] = [];
  if (Array.isArray(view.searchStatus)) {
    statuses = view.searchStatus;
  } else if (view.searchStatus) {
    statuses = [view.searchStatus];
  }
  if (statuses.length > 0) {
    const statusLabel = statuses.length === TOTAL_STATUS_COUNT ? CRITERIA_LABELS.PERIOD_ALL : statuses.join(', ');
    lines.push(`${CRITERIA_LABELS.STATUS}: ${statusLabel}`);
  }

  if (view.preparerIds.length > 0) {
    lines.push(`${CRITERIA_LABELS.PREPARERS}: ${formatUserList(view.preparerIds, staffUsers)}`);
  }
  if (view.reviewerIds.length > 0) {
    lines.push(`${CRITERIA_LABELS.REVIEWERS}: ${formatUserList(view.reviewerIds, staffUsers)}`);
  }

  const groupNames = getGroupNames([...view.preparerIds, ...view.reviewerIds], groups);
  if (groupNames.length > 0) {
    lines.push(`${CRITERIA_LABELS.GROUPS}: ${groupNames.join(', ')}`);
  }

  if (view.fromDate) {
    lines.push(`${CRITERIA_LABELS.FROM}: ${new Date(view.fromDate).toLocaleDateString()}`);
  }
  if (view.toDate) {
    lines.push(`${CRITERIA_LABELS.TO}: ${new Date(view.toDate).toLocaleDateString()}`);
  }

  return lines;
};

const ViewButtonWithTooltip = ({ savedView, currentViewId, onClick, staffUsers = [] }: ViewButtonWithTooltipProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const { groups } = useUserGroups();

  useLayoutEffect(() => {
    const checkTruncation = () => {
      if (buttonRef.current) {
        const textElement = buttonRef.current.querySelector('.button-text') as HTMLElement;
        if (textElement) {
          const truncated = textElement.scrollWidth > textElement.clientWidth;
          setIsTruncated(truncated);
        }
      }
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [savedView.name]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick(e, savedView);
  };

  const isActive = savedView.id === currentViewId;
  const criteriaLines = buildCriteriaLines(savedView, staffUsers, groups);

  const tooltipContent = (
    <Box sx={{ color: 'common.white' }}>
      {isTruncated && (
        <Typography variant="body2" fontWeight="bold" gutterBottom color="inherit">
          {savedView.name}
        </Typography>
      )}
      {criteriaLines.length > 0 ? (
        criteriaLines.map(line => (
          <Typography key={line} variant="caption" display="block" color="inherit">
            {line}
          </Typography>
        ))
      ) : (
        <Typography variant="caption" color="inherit">{CRITERIA_LABELS.NO_FILTERS}</Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow disableInteractive>
      <StyledViewButton
        ref={buttonRef}
        variant="outlined"
        color="primary"
        onClick={handleClick}
        id={savedView.id}
        isActive={isActive}
      >
        <TuneIcon />
        <span className="button-text">{savedView.name}</span>
      </StyledViewButton>
    </Tooltip>
  );
};

export default ViewButtonWithTooltip;

